/* eslint-disable react/button-has-type */
import { flattenClassNames } from '@trukt/utils/flattenClassNames';
import { css, keyframes } from '@emotion/react';
import { Link } from 'react-router-dom';
import { Icon } from '../../Icon';
import { useTheme } from '../../../hooks/useTheme';

import { useStyles } from './Button.styles';
import { ButtonProps } from './Button.types';
import { getAccessibleButtonColors } from './Button.helpers';
import { VARIANT_CLASS_NAME_MAP } from '../../Typography/Typography.constants';

const rotate = keyframes({
  '100%': { transform: 'rotate(360deg)' },
});

export const Button = ({
  boxShadowVariant = 'secondary',
  name,
  type = 'button',
  typographyVariant = 'label',
  color = 'primary',
  variant = 'contained',
  isLoading = false,
  size = 'lg',
  className,
  fullWidth = false,
  disabled = false,
  isActive = false,
  children,
  onClick,
  startIcon,
  endIcon,
  to,
  target,
  href,
  ...rest
}: ButtonProps) => {
  const styles = useStyles({
    typographyVariant,
    color,
    size,
    fullWidth,
    isActive,
    disabled,
    boxShadowVariant,
  });

  const baseClassName = flattenClassNames([
    VARIANT_CLASS_NAME_MAP[typographyVariant],
    'overflow-hidden text-ellipsis',
    className,
  ]);

  const theme = useTheme();
  const accessibleButtonColors = getAccessibleButtonColors(color, theme);
  const isButtonAwaiting = disabled || isLoading;

  if (variant === 'transparent') {
    return (
      <button
        /**
         * prevents our mouse down events from triggering parent focusable items.
         * ex) an icon within an input. We don't want to focus the input when we click the icon
         * instead we want to just have the icon click the even propagation stopped.
         */
        onMouseDown={(e) => {
          e.stopPropagation();
          rest?.onMouseDown?.(e);
        }}
        onClick={onClick}
        type={type}
        disabled={disabled}
        className={flattenClassNames([
          'text-inherit font-inherit outline-inherit m-0 inline-block cursor-pointer border-none bg-[transparent] p-0 text-left [&_*]:cursor-pointer',
          className,
        ])}
        {...rest}
      >
        {children}
      </button>
    );
  }

  const renderLink = () => {
    return href ? (
      <a
        href={href}
        css={styles.container.variants.link}
        target={target}
        className={flattenClassNames([baseClassName])}
        aria-label={rest['aria-label']}
      >
        {children}
      </a>
    ) : (
      <Link
        to={to || '#'}
        css={styles.container.variants.link}
        target={target}
        className={baseClassName}
      >
        {children}
      </Link>
    );
  };

  return (
    <>
      {variant === 'link' ? (
        renderLink()
      ) : (
        <button
          tabIndex={disabled ? -1 : 0}
          type={type}
          name={name}
          disabled={isButtonAwaiting}
          css={[styles.container.base, styles.container.variants[variant]]}
          className={baseClassName}
          onClick={isButtonAwaiting ? () => {} : onClick}
          aria-pressed={isActive}
          {...rest}
        >
          {isLoading ? (
            <Icon
              role="progressbar"
              name="loader"
              title="Button Loader"
              css={css({
                animation: `${rotate} 2s linear infinite`,
              })}
              color={accessibleButtonColors.rest}
            />
          ) : (
            <>
              {startIcon}
              {children}
              {endIcon}
            </>
          )}
        </button>
      )}
    </>
  );
};
